<template>
  <div class="layout-steps page-max-width footer-max-width">
    <QHeader class="bg-white text-primary shadow-soft full-width">
      <QToolbar>
        <QBtn flat round dense icon="icon-f-arrow-left" :disabled="loadingData" @click="goBack()" />
        <QToolbarTitle class="toolbar-subtitled">
          {{ stepTitle }}
          <p>
            {{ stepSubtitle }}
          </p>
        </QToolbarTitle>
        <QBtn v-if="$q.screen.lt.md" flat round dense icon="far fa-xmark" @click="leave()" />
      </QToolbar>
      <ProgressBarSteps v-if="currentStep > 0" :steps="totalSteps" :current-step="currentStep" color="accent" />
    </QHeader>
    <QPageContainer>
      <QPage>
        <transition appear :enter-active-class="transition.classes.enter" :leave-active-class="transition.classes.leave">
          <div v-if="currentStepData" class="creation-demand-step" :class="stepSlug === TEMPLATE_CREATION_STEPS.SUMMARIZE ? 'recap-step' : ''">
            <QPage padding>
              <component
                :is="componentToDisplay"
                :ref="stepSlug"
                :step-data="stepData"
                :step-assets="stepAssets"
                :loading="loading"
                @save-step="saveStep"
                @go-to-step="goToStep"
              />
            </QPage>
          </div>
        </transition>
      </QPage>
    </QPageContainer>
  </div>
</template>

<script setup>
  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { DataLayerManager } from "@core/managers/datalayer-manager";
  import { CreationDemandService } from "@core/services";
  import { ETAB_ROUTES } from "@etab/config";
  import { CreationStepModel } from "@core/models";
  import { FEATURE_TOGGLES, TEMPLATE_CREATION_STEPS } from "@core/constants";
  import { CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE } from "@core/store/types";
  import { UserStorageManager } from "@core/managers/user-storage.manager";
  import { useFeatureTogglesStore } from "@core/store/modules/featureToggles";
  import { ref, computed, watch, onMounted, inject } from "vue";
  import { useRouter, useRoute } from "vue-router";
  import { useQuasar } from "quasar";
  import { useI18n } from "vue-i18n";
  import { useTransition } from "@core/composables";
  import { LoadingManager } from "@core/managers/loading.manager";

  import Address from "@etab/components/creation/Address.component.vue";
  import Number from "@etab/components/creation/Number.component.vue";
  import Qualification from "@etab/components/creation/Qualification.component.vue";
  import Planning from "@etab/components/creation/Planning.component.vue";
  import Shifts from "@etab/components/creation/Shifts.component.vue";
  import Comment from "@etab/components/creation/Comment.component.vue";
  import Clothing from "@etab/components/creation/Clothing.component.vue";
  import ExperienceLevel from "@etab/components/creation/ExperienceLevel.component.vue";
  import Error from "@etab/components/creation/Error.component.vue";
  import Salary from "@etab/components/creation/Salary.component.vue";
  import PricingBoost from "@etab/components/creation/PricingBoost.component.vue";
  import Week from "@etab/components/creation/Week.component.vue";
  import Recap from "@etab/components/creation/Recap.component.vue";
  import AutoBook from "@etab/components/creation/AutoBook.component.vue";

  const { transition, transitionUpdate } = useTransition();

  const currentStepData = ref(undefined);
  const currentStep = ref(0);
  const totalSteps = ref(0);
  const loading = ref(false);
  const loadingData = ref(false);

  const $q = useQuasar();
  const { t } = useI18n();
  const router = useRouter();
  const route = useRoute();
  const $posthog = inject("$posthog");

  const posthogDemandCreationEventsEnabled = computed(() => {
    const featureTogglesStore = useFeatureTogglesStore();
    return featureTogglesStore[CORE_GETTER_FEATURE_TOGGLES_HAS_FEATURE](FEATURE_TOGGLES.POSTHOG_ETAB_TEMPLATE_FUNNEL);
  });

  const stepData = computed(() => {
    return currentStepData.value.getData();
  });

  const stepAssets = computed(() => {
    return currentStepData.value.getAssets();
  });

  const stepTitle = computed(() => {
    return currentStepData.value?.getStepTitle();
  });

  const stepSubtitle = computed(() => {
    return currentStepData.value?.getStepSubtitle();
  });

  const stepSlug = computed(() => {
    return route.params.stepSlug;
  });

  const templateId = computed(() => {
    return route.params.templateId;
  });

  const componentToDisplay = computed(() => {
    switch (stepSlug.value) {
      case TEMPLATE_CREATION_STEPS.EXTRA_AMOUNT:
        return Number;
      case TEMPLATE_CREATION_STEPS.QUALIFICATION:
        return Qualification;
      case TEMPLATE_CREATION_STEPS.PLANNING:
        return Planning;
      case TEMPLATE_CREATION_STEPS.QUOTE:
        return PricingBoost;
      case TEMPLATE_CREATION_STEPS.SHIFT:
        return Shifts;
      case TEMPLATE_CREATION_STEPS.COMMENT:
        return Comment;
      case TEMPLATE_CREATION_STEPS.ADDRESS:
        return Address;
      case TEMPLATE_CREATION_STEPS.OUTFIT:
        return Clothing;
      case TEMPLATE_CREATION_STEPS.NOVICE:
        return ExperienceLevel;
      case TEMPLATE_CREATION_STEPS.SALARY:
        return Salary;
      case TEMPLATE_CREATION_STEPS.WEEK:
        return Week;
      case TEMPLATE_CREATION_STEPS.SUMMARIZE:
        return Recap;
      case TEMPLATE_CREATION_STEPS.AUTO_BOOK:
        return AutoBook;
      default:
        return Error;
    }
  });

  // Methods
  const goBack = function () {
    transitionUpdate("LEFT");
    if (window.history.length > 1) {
      // if we didn't provide back route we check if the browser history has something for us
      router.go(-1);
    }
  };

  const leave = function () {
    router.push({ name: ETAB_ROUTES.creation.root.name }).catch((suppressedError) => {});
  };

  const getStep = async function () {
    try {
      LoadingManager.show();
      loadingData.value = true;
      currentStepData.value = undefined;
      const data = await CreationDemandService.getCreationStep({ templateId: templateId.value, stepSlug: stepSlug.value });
      currentStepData.value = new CreationStepModel(data);
      currentStep.value = currentStepData.value.getCurrentStep();
      totalSteps.value = currentStepData.value.getTotalSteps();
      // Handle datalayer for tracking purpose
      DataLayerManager.setData(
        { page_type: "formulaire", formulaire: "creation_annonce", formulaire_step_number: currentStepData.value.getCurrentStep() },
        "etablissement",
        "etablissement",
      );
    } catch (error) {
      const status = error && error.request && error.request.status;
      switch (status) {
        case 404: // leave funnel
          NotificationsManager.error({ message: "ERRORS.@CREATION_FUNNEL_404" });
          router.push({ name: ETAB_ROUTES.creation.error.name }).catch((suppressedError) => {});
          break;
        default:
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          break;
      }
    } finally {
      loadingData.value = false;
      LoadingManager.hide();
    }
  };

  const goToStep = function (slug) {
    transitionUpdate("DOWN");
    const { templateId } = route.params;
    router
      .push({
        name: ETAB_ROUTES.creation.funnel.step.slug.name,
        params: { templateId, stepSlug: slug },
        query: { reached_summarize: true },
      })
      .catch((suppressedError) => {});
  };

  const saveStep = function (data) {
    transitionUpdate("RIGHT");
    if (stepAssets.value?.warning) {
      const { templateId, query } = route.params;
      $q.dialog({
        persistent: true,
        title: t("CORE.COMPONENTS.GUARDED_ROUTE.@TITLE"),
        message: stepAssets.value.warning.message,
        ok: {
          label: t("CORE.BUTTONS.@CONFIRM"),
          noCaps: true,
        },
        cancel: {
          label: t("ETAB.PAGES.DEMANDS.CREATION.@EDIT_WARNING_CANCEL_BUTTON"),
          flat: true,
          noCaps: true,
        },
      })
        .onOk(() => {
          // in case we update data, add reset key
          save({ ...data, reset: true }, true);
        })
        .onCancel(() => {
          // got back to summarize without saving data
          router
            .push({
              name: ETAB_ROUTES.creation.funnel.step.slug.name,
              params: { templateId, stepSlug: TEMPLATE_CREATION_STEPS.SUMMARIZE },
              query,
            })
            .catch((suppressedError) => {});
        });
    } else {
      save(data);
    }
  };

  const save = async function (data, forceEdition = false) {
    try {
      loading.value = true;
      const { templateId, stepSlug } = route.params;
      const { query } = route;
      // if template has been fully completed set previousStep to make sure api redirect user to summarize after saving data
      let previousStep =
        route.query?.reached_summarize && route.params.stepSlug !== TEMPLATE_CREATION_STEPS.SUMMARIZE ? TEMPLATE_CREATION_STEPS.SUMMARIZE : null;
      // in case users force edition while having a warning, redirect user to the warning step slug instead of summarize step
      if (stepAssets.value.warning && route.query?.reached_summarize && forceEdition) {
        previousStep = stepAssets.value.warning.step_slug;
      }
      const response = await CreationDemandService.saveCreationStep({
        templateId,
        stepSlug,
        stepData: data,
        previousStep,
      });
      if ($posthog && posthogDemandCreationEventsEnabled) {
        let clientType = await UserStorageManager.getClientType();
        let params = { $step: stepSlug, $template: templateId, $client_type: clientType };
        if (TEMPLATE_CREATION_STEPS.EXTRA_AMOUNT === stepSlug && data.candidate) {
          // add extra info for booking express
          params["$chosen_candidate"] = data.candidate;
        }
        $posthog.capture("step_saved", { ...params });
      }
      // then redirect to new slug ...
      const nextStepSlug = response.slug;
      router
        .push({
          name: ETAB_ROUTES.creation.funnel.step.slug.name,
          params: { templateId, stepSlug: nextStepSlug },
          query,
        })
        .catch((suppressedError) => {});
    } catch (error) {
      const status = error && error.request && error.request.status;
      switch (status) {
        case 404: // leave funnel
          NotificationsManager.error({ message: "ERRORS.@CREATION_FUNNEL_404" });
          router.replace({ name: ETAB_ROUTES.creation.error.name }).catch((suppressedError) => {});
          break;
        default:
          NotificationsManager.error({ message: "ERRORS.@SOMETHING_WENT_WRONG" });
          break;
      }
    } finally {
      loading.value = false;
    }
  };

  // Watch
  watch(stepSlug, function () {
    getStep();
  });

  // Created
  onMounted(() => {
    getStep();
  });
</script>

<style lang="scss">
  .creation-demand-step {
    &.recap-step {
      background: none;
    }
    @media screen and (max-width: $breakpoint-xs-max) {
      &.recap-step {
        background: linear-gradient(rgba($secondary, 0.25), rgba(255, 255, 255, 0));
      }
      footer {
        button {
          border-radius: 0;
        }
      }
    }
  }
</style>
